<template>
  <div class="page-container">
    <div v-if="!showLoading" class="background-wrapper">
      <img :src="backgroundSign" class="background-image" />
      <div
        v-for="(hotspot, index) in hotspots"
        :key="index"
        class="hotspot-circle"
        :style="getHotspotStyle(hotspot)"
        @click="handleHotspotClick(hotspot, index)"
      >
        <img
          v-if="getHotspotImage(hotspot)"
          :src="getHotspotImage(hotspot)"
          class="hotspot-image"
          :style="{
            // 小程序使用 rpx，这里按约 0.5 缩放到 px，避免图片过大
            width: ((hotspot.size || 80) * 0.5) + 'px',
            height: ((hotspot.size || 80) * 0.5) + 'px'
          }"
        />
      </div>
    </div>

    <div v-if="showcheckInBox" class="checkin-overlay" @click="handleOverlayClick">
      <div class="fcc-h checkin-pop" @click.stop>
        <div class="canvas-container">
          <div
            ref="canvasWrap"
            class="canvas-wrap"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
          >
            <!-- 占位，与小程序一致：标准坐标系 800x600 在此区域触摸 -->
          </div>
          <div class="checkin-hint">盖章区域</div>
        </div>
      </div>
    </div>

    <div v-if="showLoading" class="loading-overlay">
      <div class="fcc-h loading-wrapper" style="height: 100vh;">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-logo">
            <img :src="getImageUrl('logoicon.png')" alt="" />
          </div>
        </div>
        <div class="loading-text">加载中...</div>
      </div>
    </div>

    <div v-if="load" class="loading-overlay" @click="load = false">
      <div class="loading-content" @click.stop>
        <div class="brand-logo fcc seal-loading">
          <div class="seal-circle-outer">
            <img class="circle" :src="getImageUrl('8bg.png')" alt="" />
          </div>
          <div class="seal-logo-inner">
            <img :src="getImageUrl('logoicon.png')" alt="" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showHotspotImagePreview" class="hotspot-image-preview-overlay" @click="hideHotspotImagePreview">
      <div class="hotspot-image-preview-container" @click="hideHotspotImagePreview">
        <div class="hotspot-preview-image-wrapper" @click.stop>
          <img
            v-if="previewHotspotImage"
            :src="previewHotspotImage"
            class="hotspot-preview-image"
          />
        </div>
        <div v-if="!previewHotspotImage" class="preview-placeholder-text">图片加载中...</div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getSignStoreActivityDetailByCode,
  getSignStoreActivityDetail,
  validateSeal,
  getOrCreateUserRecordH5,
  getCheckedPoints
} from '@/api/signstore'
import { getImageUrl } from '@/utils/image'
import { ensureSignstoreH5Uid } from '@/utils/signstore-h5-uid'
import { Toast } from 'vant'

/**
 * 活动打卡页（与小程序 activity3-1 逻辑、坐标系完全一致，同一配置双端使用）
 * 打卡坐标系：标准 800x600，触摸点转换为 (x/rect.width)*800, (y/rect.height)*600
 */
export default {
  name: 'Activity3_1',
  data() {
    return {
      activityId: 0,
      activityCode: null,
      h5Uid: null,
      activityUserId: null,
      ruleCode: null,
      backgroundSign: '',
      hotspots: [],
      checkedPointIndexes: [],
      currentHotspotIndex: null,
      completedCount: 0,
      verifyableCount: 0,
      isVerifying: false,
      showLoading: false,
      checking: false,
      showcheckInBox: false,
      load: false,
      showHotspotImagePreview: false,
      previewHotspotImage: '',
      standardWidth: 800,
      standardHeight: 600,
      debounceTimer: null,
      lastTouchPoints: null
    }
  },
  mounted() {
    const activityCode = this.$route.query.activity_code
    const activityId = this.$route.query.id
    const uidRaw = this.$route.query.uid != null ? String(this.$route.query.uid).trim() : ''
    if (!uidRaw) {
      this.h5Uid = ensureSignstoreH5Uid(this.$route.query.h5_uid)
    }
    if (activityCode) {
      this.activityCode = activityCode
      this.queryActiveFun()
    } else if (activityId) {
      this.activityId = activityId
      this.queryActiveFun()
    } else {
      Toast('活动不存在或已结束')
      setTimeout(() => this.$router.replace('/index'), 1500)
    }
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  },
  methods: {
    getImageUrl,
    getHotspotStyle(hotspot) {
      return {
        left: (hotspot.left !== undefined ? hotspot.left : 0) + '%',
        top: (hotspot.top !== undefined ? hotspot.top : 0) + '%',
        width: (hotspot.size || 80) + 'px',
        height: (hotspot.size || 80) + 'px'
      }
    },
    async queryActiveFun() {
      const self = this
      self.showLoading = true
      try {
        let res
        if (self.activityCode) {
          res = await getSignStoreActivityDetailByCode({ activityCode: self.activityCode })
        } else if (self.activityId) {
          res = await getSignStoreActivityDetail({ id: self.activityId })
        } else {
          throw new Error('活动ID或编码不存在')
        }
        if (!res || res.status !== 200) throw new Error((res && res.msg) || '获取活动详情失败')
        const activityData = res.data
        if (!activityData) throw new Error('活动数据为空')

        self.activityId = activityData.id
        if (activityData.activityCode) self.activityCode = activityData.activityCode
        self.ruleCode = activityData.ruleCode || null

        let pageConfig = activityData.pageConfig || {}
        if (activityData.pageConfigJson) {
          try {
            pageConfig = typeof activityData.pageConfigJson === 'string'
              ? JSON.parse(activityData.pageConfigJson)
              : activityData.pageConfigJson
          } catch (e) {
            console.error('解析 pageConfigJson 失败:', e)
          }
        }

        self.backgroundSign = getImageUrl(pageConfig.backgroundSign || '') || pageConfig.backgroundSign || ''
        if (!self.backgroundSign) Toast('未配置背景图')

        if (pageConfig.hotspots && Array.isArray(pageConfig.hotspots) && pageConfig.hotspots.length > 0) {
          self.hotspots = pageConfig.hotspots.map(h => ({
            left: (h.left != null ? h.left : (h.x != null ? h.x : 0)),
            top: (h.top != null ? h.top : (h.y != null ? h.y : 0)),
            size: h.size || 80,
            index: (h.index != null ? h.index : null),
            checkType: h.checkType || 'CHECKIN',
            doSignImg: h.doSignImg || '',
            notSignImg: h.notSignImg || ''
          }))
        }

        await self.loadUserRecord()
        await self.loadCheckedPoints()
        self.showLoading = false
      } catch (error) {
        self.showLoading = false
        Toast(error.message || '加载活动失败')
      }
    },
    async loadUserRecord() {
      try {
        const params = {}
        if (this.activityId) params.activityId = this.activityId
        else if (this.activityCode) params.activityCode = this.activityCode
        if (this.h5Uid) params.h5Uuid = this.h5Uid
        else return
        const res = await getOrCreateUserRecordH5(params)
        if (res && res.status === 200 && res.data) {
          const userRecord = res.data
          this.activityUserId = userRecord.id || null
          this.completedCount = userRecord.completedCount || 0
          this.verifyableCount = userRecord.verifyableCount || 0
        }
      } catch (error) {
        this.activityUserId = null
        this.completedCount = 0
        this.verifyableCount = 0
      }
    },
    async loadCheckedPoints() {
      if (!this.activityUserId) return
      try {
        const res = await getCheckedPoints({ activityUserId: this.activityUserId })
        if (res && res.status === 200 && res.data) {
          this.checkedPointIndexes = Array.isArray(res.data) ? res.data : []
        }
      } catch (error) {
        this.checkedPointIndexes = []
      }
    },
    getHotspotImage(hotspot) {
      if (!hotspot) return ''
      if (hotspot.index === null || hotspot.index === undefined) {
        return hotspot.notSignImg || ''
      }
      if (this.checkedPointIndexes.includes(hotspot.index)) {
        return hotspot.doSignImg || hotspot.notSignImg || ''
      }
      return hotspot.notSignImg || ''
    },
    handleHotspotClick(hotspot, index) {
      this.currentHotspotIndex = hotspot.index != null ? hotspot.index : null
      const isChecked = hotspot.index != null && this.checkedPointIndexes.includes(hotspot.index)
      if (isChecked && hotspot.doSignImg) {
        this.previewHotspotImage = getImageUrl(hotspot.doSignImg) || hotspot.doSignImg
        this.showHotspotImagePreview = true
        return
      }
      if (hotspot.checkType === 'VERIFY') {
        if (!this.checkAllCheckinCompleted()) {
          Toast('请先完成所有打卡任务')
          return
        }
        this.isVerifying = true
        this.showcheckInBox = true
      } else {
        this.isVerifying = false
        this.showcheckInBox = true
      }
    },
    checkAllCheckinCompleted() {
      const checkinHotspots = this.hotspots.filter(
        h => h.checkType === 'CHECKIN' && h.index != null
      )
      if (checkinHotspots.length === 0) return true
      return checkinHotspots.every(h => this.checkedPointIndexes.includes(h.index))
    },
    hideHotspotImagePreview() {
      this.showHotspotImagePreview = false
      this.previewHotspotImage = ''
    },
    handleOverlayClick() {
      this.showcheckInBox = false
    },
    handleTouchStart(event) {
      if (!event.touches || event.touches.length !== 4) return
      if (this.checking) return
      event.preventDefault()
      const el = this.$refs.canvasWrap
      if (!el) return
      const rect = el.getBoundingClientRect()
      if (!rect || rect.width === 0 || rect.height === 0) {
        Toast('无法获取打卡区域位置，请重试')
        return
      }
      const touchPoints = []
      for (let i = 0; i < event.touches.length; i++) {
        const t = event.touches[i]
        const x = t.clientX - rect.left
        const y = t.clientY - rect.top
        const standardX = Math.round((x / rect.width) * this.standardWidth)
        const standardY = Math.round((y / rect.height) * this.standardHeight)
        touchPoints.push([standardX, standardY])
      }
      this.lastTouchPoints = touchPoints
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.doValidateSeal(this.lastTouchPoints)
        this.debounceTimer = null
      }, 1000)
    },
    handleTouchEnd(event) {
      if (event.touches && event.touches.length > 0) return
      if (this.debounceTimer && this.lastTouchPoints) {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = null
        this.doValidateSeal(this.lastTouchPoints)
      }
    },
    async doValidateSeal(touchArr) {
      if (this.checking) return
      this.checking = true
      this.load = true
      try {
        if (!this.ruleCode) {
          Toast('活动规则编码不存在，无法打卡')
          this.load = false
          this.checking = false
          return
        }
        if (!this.activityUserId) await this.loadUserRecord()
        if (!this.activityUserId) {
          Toast('用户活动记录不存在，无法打卡')
          this.load = false
          this.checking = false
          return
        }
        const validateData = {
          ruleCode: this.ruleCode,
          touchPoints: touchArr,
          activityUserId: this.activityUserId,
          operationType: this.isVerifying ? 'VERIFY' : 'CHECKIN',
          pointIndex: this.currentHotspotIndex != null ? this.currentHotspotIndex : null
        }
        const validateRes = await validateSeal(validateData)
        const result = validateRes && validateRes.data !== undefined ? validateRes.data : validateRes
        if (!result || !result.matched) {
          const message = (result && result.errorMessage) || (result && result.message) || '印章验证失败，请重试'
          Toast(message)
          this.load = false
          this.checking = false
          return
        }
        this.showcheckInBox = false
        this.checking = false
        await this.loadUserRecord()
        await this.loadCheckedPoints()
        if (this.isVerifying) {
          Toast.success('核销成功')
          this.isVerifying = false
        }
        this.currentHotspotIndex = null
        setTimeout(() => { this.load = false }, 2000)
      } catch (error) {
        Toast('打卡失败，请重试')
        this.load = false
        this.checking = false
      }
    }
  }
}
</script>

<style scoped lang="less">
.page-container {
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: #f5f5f5;
  padding: 0;
  margin: 0;
}

.background-wrapper {
  position: relative;
  width: 100%;
  min-height: 0;
  padding-bottom: calc(1334 / 750 * 100%);
  background: #f5f5f5;
}

.background-image {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: auto;
  display: block;
}

.hotspot-circle {
  position: absolute;
  border-radius: 50%;
  z-index: 10;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.hotspot-circle:active {
  transform: translate(-50%, -50%) scale(0.95);
  opacity: 0.8;
}

.hotspot-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: contain;
}

.fcc-h {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.fcc {
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkin-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkin-pop {
  /* 整个盖章弹窗区域最大化，几乎占满屏幕宽度 */
  width: 100%;
  max-width: 640px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.canvas-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.canvas-wrap {
  /* 盖章区域最大化，参照小程序 600rpx × 600rpx，保持接近正方形比例 */
  width: min(100vw, 600px);
  height: min(100vw, 600px);
  border: 2px solid #ddd;
  border-radius: 8px;
  background: #f5f5f5;
  box-sizing: border-box;
  touch-action: none;
}

.checkin-hint {
  font-size: 14px;
  color: #666;
  margin-top: 10px;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-wrapper,
.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.loading-spinner {
  position: relative;
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: rgba(255, 255, 255, 0.8);
  border-right-color: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  animation: spinner-rotate 1.2s linear infinite;
}
.spinner-ring:nth-child(2) {
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-delay: 0.15s;
}
.spinner-ring:nth-child(3) {
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-delay: 0.3s;
}

.spinner-logo {
  position: absolute;
  width: 50px;
  height: 50px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.spinner-logo img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.loading-text {
  color: #fff;
  font-size: 14px;
}

.seal-loading {
  width: 200px;
  height: 200px;
  position: relative;
}
.seal-circle-outer {
  position: absolute;
  width: 100%;
  height: 100%;
  animation: seal-rotate 3s linear infinite;
}
.seal-circle-outer .circle {
  width: 100%;
  height: 100%;
}
.seal-logo-inner {
  position: relative;
  width: 100px;
  height: 100px;
  z-index: 2;
}
.seal-logo-inner img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

@keyframes spinner-rotate {
  to { transform: rotate(360deg); }
}
@keyframes seal-rotate {
  to { transform: rotate(360deg); }
}

.hotspot-image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hotspot-image-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.hotspot-preview-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 85vw;
}

.hotspot-preview-image {
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
}

.preview-placeholder-text {
  color: #fff;
  font-size: 14px;
}
</style>
